import Radium from '@flipdish/radium';
import { debounce } from 'lodash';
import * as Raven from 'raven-js';
import * as React from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import { findDOMNode } from 'react-dom';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { AddLocation } from '../../actions/Actions';
import * as Api from '../../Api';
import * as Data from '../../Data';
import * as Store from '../../Store';
import * as Utilities from '../../Utilities';
import {
  BrowserHasLocation,
  EnsureVisible,
  extraOption,
  iOS
} from '../../Utilities';
import {
  PageTitle,
  SectionHeader,
  SectionHeaderIcon,
  Icon,
  SectionHeaderText
} from '../../../ui';
const tinycolor = require('tinycolor2');

interface DispatchProps {
  AddLocation: (location: Api.Geo, certainty: Store.LocationCertainty) => any;
}

interface StateProps {
  Config: Data.Config;
  AppConfig: Data.Async<Api.AppConfig>;
  Location: Api.Geo;
  translate: any;
  AppDesignScheme: Api.AppDesignScheme;
  isDark: boolean;
}

type Props = DispatchProps & StateProps;

interface State {
  map: google.maps.Map | null;
  places: google.maps.places.PlacesService | null;
  geolocationInProgress: boolean;
  suggestions: google.maps.places.AutocompletePrediction[];
  selected: google.maps.places.AutocompletePrediction | null;
}

// Get the input string from a HTML input event
const targetInput = function(
  e: React.FormEvent<React.Component<any, any>>
): string {
  const input = (e.target as any).value;
  return input as string;
};

class Map extends React.Component<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context);
    this.state = {
      map: null,
      places: null,
      selected: null,
      geolocationInProgress: false,
      suggestions: []
    };
  }

  public componentDidMount() {
    EnsureVisible(findDOMNode(this));
    this.props.Config.MapLoader.then(this.initMap);
  }

  public findUserLocation = () => {
    if (!BrowserHasLocation()) {
      alert(
        'Your browser does not support geolocation lookup. We cannot use your location - please enter your address instead.'
      );
      Raven.captureMessage('Geolocation not supported', {
        tags: { category: 'Third party library failure' }
      });
      return;
    }

    this.props.Config.MapLoader.then(() => {
      const { map } = this.state;
      if (map) {
        this.setState({ geolocationInProgress: true });
        navigator.geolocation.getCurrentPosition(
          p => this.handleGeolocationSuccess(map, p),
          this.handleGeolocationError,
          {
            enableHighAccuracy: true
          }
        );
      }
    });
  };

  public initMap = (tools: Data.GoogleTools) => {
    const options: google.maps.MapOptions = {
      center: Data.LatLng(this.props.Location),
      mapTypeControl: false, // No map type (terrain/satelite, etc)
      streetViewControl: false, // No street view ControlLabel
      zoom: 17,
      clickableIcons: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER
      }
    };

    const centerChanged = debounce(() => {
      this.props.AddLocation(
        Data.ToGeo(map.getCenter()),
        Store.LocationCertainty.User
      );
    }, 500);

    centerChanged();

    const map = new google.maps.Map(
      document.getElementById('mapRef') as Element,
      options
    );

    this.setState({
      map,
      places: new google.maps.places.PlacesService(map)
    });

    map.addListener('center_changed', centerChanged);
  };

  public handleGeolocationError = (error: PositionError) => {
    const { translate } = this.props;

    alert(
      `${translate('Error_location')}.\n\n${translate(
        'Enter_address'
      )}.\n\n${translate('Error')} ${
        error.code === 1
          ? translate('Denied_geolocation')
          : error.code === 2
          ? translate('Permission_geolocation')
          : error.message
      }`
    );

    this.setState({ geolocationInProgress: false });

    Raven.captureMessage('Geolocation error callback', {
      tags: { category: 'Third party library failure' },
      extra: {
        error: Utilities.handleError(error)
      }
    });
  };

  public handleGeolocationSuccess = (
    map: google.maps.Map,
    position: Position
  ) => {
    console.log('Geo lookup complete:', position);

    const location = new google.maps.LatLng(
      position.coords.latitude,
      position.coords.longitude
    );
    const center = Data.ToGeo(location);

    this.setState({
      geolocationInProgress: false
    });

    map.panTo(location);

    this.props.AddLocation(center, Store.LocationCertainty.GeoLocation);
  };

  public handlePlaceSelected = (
    locations: google.maps.places.AutocompletePrediction[]
  ) => {
    console.log('user selected', locations);

    if (!locations || locations.length === 0) {
      return;
    }

    const { map, places } = this.state;

    if (!map || !places) {
      // Can't do a lookup until these are loaded.
      // I don't think this can ever happen.
      return;
    }

    const selected = locations[0];

    const request: any = {
      placeId: selected.place_id,
      fields: ['geometry', 'address_component']
    };
    places.getDetails(request, (result, status) => {
      this.props.AddLocation(
        Data.ToGeo(result.geometry.location),
        Store.LocationCertainty.User
      );
      map.panTo(result.geometry.location);
    });

    this.setState({ selected });
  };

  private nonEmptyStringValidation = (field: string): 'success' | 'error' => {
    if (field) {
      return 'success';
    }
    return 'error';
  };

  public navigationEnabled = (): boolean => {
    const { map, geolocationInProgress } = this.state;
    return map != null && !geolocationInProgress;
  };

  public handleInputChange = debounce((input: string) => {
    if (input.length < 5) {
      // Don't sent input with length less than 5 to the gepcode API.
      return;
    }

    this.props.Config.MapLoader.then(tools => {
      const { AppConfig } = this.props;
      let bounds: google.maps.LatLngBoundsLiteral | undefined;
      if (AppConfig.status === 'success') {
        const { data } = AppConfig;
        bounds = {
          // North East : bigger
          // South West : smaller
          north: data.MapBoundryMaxLatitude,
          south: data.MapBoundryMinLatitude,
          east: data.MapBoundryMaxLongitude,
          west: data.MapBoundryMinLongitude
        };
      }

      console.log(this.props.Location);
      console.log({
        input,
        location: Data.LatLng(this.props.Location),
        radius: 200, // TODO: tweak 200m?
        bounds
      });

      tools.AutoComplete.getPlacePredictions(
        {
          input,
          location: Data.LatLng(this.props.Location),
          radius: 200, // TODO: tweak 200m?
          bounds
        },
        (suggestions: google.maps.places.AutocompletePrediction[]) => {
          console.log('Got location based suggestions:', suggestions);

          if (suggestions) {
            // suggestions can be null if given invalid input.
            // The <Typeahead> component cannot deal with null.
            this.setState({ suggestions });
          }
        }
      );
    });
  }, 250);

  public render(): JSX.Element {
    const { translate, AppDesignScheme, isDark } = this.props;

    const { geolocationInProgress } = this.state;

    const styles = {
      primary: {
        color: AppDesignScheme.Primary
      },
      secondary: {
        color: AppDesignScheme.Secondary
      },
      tertiary: {
        color: AppDesignScheme.Tertiary
      },
      nav: {
        ':hover': {},
        ':active': {}
      },
      secondaryActive: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString()
      },
      secondaryHover: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString()
      },
      subheader: {
        borderColor: AppDesignScheme.Secondary,
        boxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        backgroundColor: tinycolor(AppDesignScheme.Primary)
          .darken(3)
          .toString()
      },
      darkSubheader: {
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString(),
        backgroundColor: tinycolor(AppDesignScheme.Tertiary)
          .lighten(10)
          .toString(),
        boxShadow: `1px 1px 9px -5px ${tinycolor(
          AppDesignScheme.Tertiary
        ).darken(10)}`
      },
      borderSecondary: {
        borderColor: AppDesignScheme.Secondary
      },
      buttonPrimary: {
        backgroundColor: tinycolor(AppDesignScheme.Primary)
          .darken(3)
          .toString(),
        borderColor: AppDesignScheme.Secondary,
        ':hover': {
          borderColor: AppDesignScheme.Secondary
        },
        ':active': {},
        ':focus': {
          borderColor: AppDesignScheme.Secondary
        }
      },
      buttonSecondary: {
        backgroundColor: AppDesignScheme.Secondary,
        ':hover': {},
        ':active': {},
        ':focus': {}
      },
      buttonActive: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString()
      },
      buttonPrimaryHover: {
        backgroundColor: AppDesignScheme.Secondary
      },
      buttonSecondaryHover: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString()
      }
    };

    const geolocationTitle = geolocationInProgress
      ? translate('Finding_location')
      : translate('Use_your_current_location');

    let geolocationButton: JSX.Element | null = null;

    let orSeparator: JSX.Element | null = null;

    if (BrowserHasLocation()) {
      geolocationButton = (
        <button
          className="fd-button fd-button--primary flex-one"
          disabled={!this.navigationEnabled()}
          onClick={this.findUserLocation}
          key="geolocation-button"
          style={
            [
              styles.buttonPrimary,
              Radium.getState(this.state, 'geolocation-button', ':active')
                ? styles.buttonActive
                : Radium.getState(this.state, 'geolocation-button', ':hover')
                ? styles.buttonPrimaryHover
                : null
            ] as any
          }
        >
          <span className="flex flex-align-center flex-justify-center">
            <i
              className="secondary icon_pin"
              aria-hidden="true"
              style={
                Radium.getState(this.state, 'geolocation-button', ':hover') ||
                Radium.getState(this.state, 'geolocation-button', ':active')
                  ? styles.primary
                  : styles.secondary
              }
            />
            <p
              className="clear-margins secondary"
              style={
                Radium.getState(this.state, 'geolocation-button', ':hover') ||
                Radium.getState(this.state, 'geolocation-button', ':active')
                  ? styles.primary
                  : styles.secondary
              }
            >
              {geolocationTitle}
            </p>
          </span>
        </button>
      );

      orSeparator = (
        <div className="flex flex-align-center flex-justify-center map__actions__or">
          <hr style={styles.borderSecondary} />
          <p className="clear-margins tertiary fd-h4" style={styles.tertiary}>
            {translate('Or')}
          </p>
          <hr style={styles.borderSecondary} />
        </div>
      );
    }

    const next =
      '/delivery/custom/' +
      this.props.Location.Latitude +
      '/' +
      this.props.Location.Longitude;

    return (
      <div className="container-fluid flex flex-column fill-height-vh">
        <div className="container phone flex-one xs-full-width">
          <div className="row map">
            <div className="col-md-12">
              <div style={{ marginTop: '20px' }} />

              <PageTitle>{translate('Enter_your_address')}</PageTitle>

              <Link to="/delivery/new">
                <div
                  key="fd-nav"
                  className="fd-nav flex flex-align-center"
                  style={styles.nav as any}
                >
                  <i
                    className="arrow_carrot-left tertiary"
                    style={
                      Radium.getState(this.state, 'fd-nav', ':active')
                        ? styles.secondaryActive
                        : Radium.getState(this.state, 'fd-nav', ':hover')
                        ? styles.secondaryHover
                        : isDark
                        ? styles.primary
                        : styles.tertiary
                    }
                    aria-hidden="true"
                  />
                  <p
                    className="inline-block clear-margins tertiary bold"
                    style={
                      Radium.getState(this.state, 'fd-nav', ':active')
                        ? styles.secondaryActive
                        : Radium.getState(this.state, 'fd-nav', ':hover')
                        ? styles.secondaryHover
                        : isDark
                        ? styles.primary
                        : styles.tertiary
                    }
                  >
                    <u>{translate('Select_a_different_location')}</u>
                  </p>
                </div>
              </Link>
            </div>
            <div className="col-md-8 col-lg-8 col-sm-12">
              <SectionHeader>
                <SectionHeaderIcon>
                  <Icon
                    fontSize="large"
                    aria-hidden
                    color="inherit"
                    className="ei icon_pin"
                  />
                </SectionHeaderIcon>

                <SectionHeaderText
                  title={translate('Delivery')}
                  subTitle={translate('Type_your_address')}
                />
              </SectionHeader>

              <div className="flex flex-align-center flex-justify-between map__actions">
                {geolocationButton}
                {orSeparator}
                <Typeahead
                  inputProps={{ id: 'mapLocation' }}
                  className="flex-one fd-typeahead"
                  labelKey="description"
                  onChange={this.handlePlaceSelected}
                  onInputChange={this.handleInputChange}
                  options={this.state.suggestions}
                  placeholder={translate('Enter_your_address_here')}
                  emptyLabel={translate('Enter_your_address_here')}
                  filterBy={() => {
                    return true;
                  }}
                  // selected={[this.state.selected]}
                />
              </div>

              <div
                tabIndex={-1}
                id="mapRef"
                className="map__location"
                style={styles.borderSecondary}
              />

              <div className="text-center">
                <Link
                  to={next}
                  style={{ textDecoration: 'none', display: 'inline-block' }}
                >
                  <div
                    className="div-styles-as-button fd-button fd-button--big fd-button--secondary hidden-xs hidden-sm"
                    key="confirm-button"
                    style={
                      [
                        styles.buttonSecondary,
                        Radium.getState(this.state, 'confirm-button', ':active')
                          ? styles.buttonActive
                          : Radium.getState(
                              this.state,
                              'confirm-button',
                              ':hover'
                            )
                          ? styles.buttonSecondaryHover
                          : null
                      ] as any
                    }
                    data-fd="address-confirm"
                  >
                    <p className="clear-margins primary" style={styles.primary}>
                      {translate('Confirm_address')}
                    </p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div
            className={`div-styles-as-button-mobile fd-button fd-button--bottom fd-button--secondary visible-xs-block visible-sm-block ${
              iOS || extraOption('data-full-screen-on-mobile') === 'false'
                ? 'fd-button--bottom-ios'
                : ''
            }`}
            key="confirm-button-bottom"
            style={
              [
                styles.buttonSecondary,
                Radium.getState(this.state, 'confirm-button-bottom', ':active')
                  ? styles.buttonActive
                  : Radium.getState(
                      this.state,
                      'confirm-button-bottom',
                      ':hover'
                    )
                  ? styles.buttonSecondaryHover
                  : null
              ] as any
            }
            data-fd="address-confirm-bottom"
          >
            <Link to={next}>
              <p className="clear-margins primary" style={styles.primary}>
                {translate('Confirm_address')}
              </p>
            </Link>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: Store.State): StateProps => {
  return {
    Config: state.flipdish.Config,
    AppConfig: state.flipdish.AppConfig,
    Location: state.flipdish.Location.geo,
    translate: getTranslate(state.locale),
    AppDesignScheme: state.appDesignScheme,
    isDark: state.flipdish.isDark
  };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => {
  return {
    AddLocation: (location: Api.Geo, certainty: Store.LocationCertainty) => {
      dispatch(AddLocation(location, certainty));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Radium(Map));
